import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import AuthenticationWrapper, { AircraftAuthenticationWrapper } from '../../components/_utils/AuthenticationWrapper';
import { DashboardResource, FeatureFlag, UserPermission } from '../../models/userSettings';
import { AircraftPermission, AircraftResource } from '../../models/aircraft';
import { Capability } from '../../models/people';
import useQueryGetCapabilities from '../../services/hooks/people/useQueryGetCapabilities';
import useAircraftMenuMap from './useAircraftMenuMap';

interface AircraftMenuNewProps {
  selectedKeys: string[];
  requiredSettings: {
    intermittentFaultRename: string;
    dentAndBuckleRename: string;
    aircraftId: string;
    cabinIssueEnabled: boolean;
    cabinIssuesOverride: string;
    defectsNameOverride: string;
    notesToCrewOverride: string;
    workpackPluralOverride: string;
    processApprovalMethodsOverride: string[];
  };
}

interface MenuBlock {
  key: string;
  requiredResource: DashboardResource | AircraftResource;
  nameOverride?: string;
  requiredFeatureFlag?: FeatureFlag;
  requiredPermissionLevel?: UserPermission;
  onlyAdmin?: boolean;
  capabilities?: Capability[];
}

const MenuWrapper = styled.div`
  height: calc(100% - 60px);
  overflow: auto;
  padding-bottom: 20px;
`;

const StyledLink = styled(Link)`
  display: flex;
  padding: 18px 20px;
  font-weight: 300;
  width: 100%;
  color: ${({ theme, active }) => (active ? theme.colours.white : theme.colours.innerMenuWhite)};
  background-color: ${({ theme, active }) => (active ? theme.colours.brandAccentBlue : 'transparent')};
  list-style-type: none;
  transition: color 0.3s;
  &:hover {
    color: ${({ theme, active }) => (active ? theme.colours.innerMenuWhite : theme.colours.white)};
  }
`;

const StyledImg = styled.img`
  width: 16px;
  margin-right: 15px;
`;

const AircraftMenuNew: React.FC<AircraftMenuNewProps> = ({ selectedKeys, requiredSettings }) => {
  const { data: capabilities } = useQueryGetCapabilities();

  const MENU_BUILD_BLOCKS: MenuBlock[] = [
    {
      key: 'trips',
      requiredResource: AircraftResource.TRIP,
    },
    {
      key: 'logbook',
      requiredResource: AircraftResource.LOGBOOK,
    },
    {
      key: 'defects/log',
      requiredResource: AircraftResource.DEFECT,
      nameOverride: requiredSettings?.defectsNameOverride,
    },
    {
      key: 'intermittent_faults',
      requiredResource: AircraftResource.DEFECT,
      nameOverride: requiredSettings?.intermittentFaultRename,
      requiredFeatureFlag: FeatureFlag.INTERMITTENT,
    },
    {
      key: 'maintenance',
      requiredResource: AircraftResource.SCHEDULED_MX_ITEM,
    },
    {
      key: 'workpacks/log',
      requiredResource: AircraftResource.WORKPACK,
      nameOverride: requiredSettings?.workpackPluralOverride,
    },
    {
      key: 'documents',
      requiredResource: AircraftResource.DOCUMENT,
      requiredFeatureFlag: FeatureFlag.DOCUMENTS,
    },
    {
      key: 'dent_and_buckle',
      requiredResource: AircraftResource.DEFECT,
      nameOverride: requiredSettings?.dentAndBuckleRename,
      requiredFeatureFlag: FeatureFlag.DENTANDBUCKLE,
    },
    {
      key: 'metrics',
      requiredResource: AircraftResource.TRIP,
    },
    {
      key: 'notes',
      requiredResource: AircraftResource.AIRCRAFT,
      requiredFeatureFlag: FeatureFlag.NOTES,
      nameOverride: requiredSettings?.notesToCrewOverride,
    },
    {
      key: 'custom_fields',
      requiredResource: AircraftResource.AIRCRAFT,
      requiredFeatureFlag: FeatureFlag.CUSTOMFIELDS,
      onlyAdmin: true,
    },
  ];

  const oneTimeBlock = {
    key: 'totp',
    requiredResource: AircraftResource.AIRCRAFT,
    capabilities: capabilities || [],
  };

  const overviewBlock = {
    key: 'overview',
  };

  const settingsBlock = {
    key: 'settings',
    requiredPermissionLevel: UserPermission.UPDATE,
  };

  if (requiredSettings.cabinIssueEnabled) {
    MENU_BUILD_BLOCKS.splice(5, 0, {
      key: 'cabin_log',
      requiredResource: AircraftResource.DEFECT,
      nameOverride: requiredSettings?.cabinIssuesOverride,
    });
  }

  const buildAuthenticationLink = (block) => {
    return (
      <AuthenticationWrapper
        requiredResource={DashboardResource.AIRCRAFT}
        requiredPermissionLevel={block.requiredPermissionLevel || UserPermission.READ}
      >
        <div data-testid={`AircraftMenuNew--Link-${block.key}`}>
          <StyledLink
            active={selectedKeys[0] === block.key}
            to={`/aircraft/${requiredSettings.aircraftId}/${block.key}`}
          >
            <StyledImg src={useAircraftMenuMap.get(block.key)?.icon} alt={`${block.key}Icon`} />
            {useAircraftMenuMap.get(block.key)?.title}
          </StyledLink>
        </div>
      </AuthenticationWrapper>
    );
  };

  const buildAircraftMenuLinks = (menuBlocks) => {
    return menuBlocks.map((block) => {
      return (
        <AircraftAuthenticationWrapper
          aircraftId={requiredSettings.aircraftId}
          requiredResource={block.requiredResource}
          requiredPermissionLevel={block.requiredPermissionLevel || AircraftPermission.READ}
          requiredFeatureFlag={block.requiredFeatureFlag}
          onlyAdmin={block.onlyAdmin}
          capabilities={block.capabilities}
        >
          <div data-testid={`AircraftMenuNew--Link-${block.key}`}>
            <StyledLink
              active={selectedKeys[0] === block.key}
              to={`/aircraft/${requiredSettings.aircraftId}/${block.key}`}
            >
              <StyledImg src={useAircraftMenuMap.get(block.key)?.icon} alt={`${block.key}Icon`} />
              {block.nameOverride || useAircraftMenuMap.get(block.key)?.title}
            </StyledLink>
          </div>
        </AircraftAuthenticationWrapper>
      );
    });
  };

  return (
    <MenuWrapper>
      {buildAuthenticationLink(overviewBlock)}
      {buildAircraftMenuLinks(MENU_BUILD_BLOCKS)}
      {buildAuthenticationLink(settingsBlock)}
      {requiredSettings?.processApprovalMethodsOverride?.includes('totp')
        ? buildAircraftMenuLinks([oneTimeBlock])
        : null}
    </MenuWrapper>
  );
};

export default AircraftMenuNew;
